import { useCallback, useState } from 'react';
import api from '@/axios';
import { pomConfig } from '@/configs/pom-config';

async function fetchDocumentBlob(clientUrl: string): Promise<Blob> {
  const apiOrigin = new URL(pomConfig.http.api).origin;
  const resolved = new URL(clientUrl, pomConfig.http.api);

  if (resolved.origin === apiOrigin) {
    const path = `${resolved.pathname}${resolved.search}`;
    const { data } = await api.get<Blob>(path, { responseType: 'blob' });
    return data;
  }

  const response = await fetch(clientUrl);
  if (!response.ok) {
    throw new Error('fetch failed');
  }

  return response.blob();
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

export async function downloadFile(
  clientUrl: string,
  fileName: string,
): Promise<void> {
  const blob = await fetchDocumentBlob(clientUrl);
  triggerBlobDownload(blob, fileName);
}

type UseDownloadFileParams = {
  clientUrl: string | null;
  fileName: string;
  onError?: () => void;
};

export function useDownloadFile({
  clientUrl,
  fileName,
  onError,
}: UseDownloadFileParams): {
  handleDownload: () => Promise<void>;
  isPending: boolean;
} {
  const [isPending, setIsPending] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!clientUrl || isPending) {
      return;
    }

    setIsPending(true);
    try {
      await downloadFile(clientUrl, fileName);
    } catch {
      onError?.();
    } finally {
      setIsPending(false);
    }
  }, [clientUrl, fileName, isPending, onError]);

  return {
    handleDownload,
    isPending,
  };
}
